<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { formatDateTime } from "@/composables/formatUtils";
import CnmcReasonForm from "@/dashboard/production_strategy/issuance-reporting/es/CnmcReasonForm.vue";
import type { CnmcAcceptedMatch } from "@/dashboard/production_strategy/issuance-reporting/es/cnmcTypes";
interface ReceiveOrder {
  id: number;
  company_id: number;
  company_name: string;
  status: string;
  verified: boolean;
  expected: {
    total_mwh: number;
    receiving_connection_id: number;
    expected_plants: { name: string; go_count: number }[];
  };
  accepted: CnmcAcceptedMatch | null;
}
interface InventoryRow {
  id: number;
  count: number;
  name: string;
  imported_at: string;
  start: string;
  end: string;
  origin: string;
}
const base = "/admin/issuance-reporting/cnmc/receive-orders";
const orders = ref<ReceiveOrder[]>([]);
const selected = ref<ReceiveOrder | null>(null);
const inventory = ref<InventoryRow[]>([]);
const selectedIds = ref<number[]>([]);
const aliases = ref<Record<number, string>>({});
const busy = ref(false);
const { globalErrorHandler } = useGlobalHandlers();
const selectedCount = computed(() =>
  inventory.value
    .filter((row) => selectedIds.value.includes(row.id))
    .reduce((sum, row) => sum + row.count, 0)
);
const load = async () => {
  busy.value = true;
  try {
    const response = await goSolidApi.get<{ orders: ReceiveOrder[] }>(base);
    orders.value = response.data.orders;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    busy.value = false;
  }
};
const review = async (order: ReceiveOrder) => {
  selected.value = order;
  selectedIds.value = [];
  inventory.value = [];
  aliases.value = {};
  if (order.status !== "PENDING") return;
  busy.value = true;
  try {
    inventory.value = (
      await goSolidApi.get<{ certificates: InventoryRow[] }>(
        `${base}/${order.id}/inventory`
      )
    ).data.certificates;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    busy.value = false;
  }
};
const accept = async (reason: string) => {
  if (
    busy.value ||
    !selected.value?.verified ||
    selectedCount.value !== selected.value.expected.total_mwh
  )
    return;
  busy.value = true;
  try {
    const plantNames = Object.fromEntries(
      Object.entries(aliases.value).filter(
        ([id, name]) => selectedIds.value.includes(Number(id)) && name.trim()
      )
    );
    await goSolidApi.post(`${base}/${selected.value.id}/acceptance`, {
      certificate_ids: selectedIds.value,
      reason,
      plant_names: plantNames,
    });
    selected.value = null;
    await load();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    busy.value = false;
  }
};
onMounted(load);
</script>
<template>
  <section v-if="orders.length" class="card mt-4">
    <div class="card-body">
      <div class="d-flex justify-content-between">
        <h5>{{ $t("cnmc.arrival_review") }}</h5>
        <button class="btn btn-link btn-sm" :disabled="busy" @click="load()">
          {{ $t("cnmc.check_status") }}
        </button>
      </div>
      <p>{{ $t("cnmc.arrival_review_help") }}</p>
      <div class="table-responsive">
        <table class="table table-padded table-sm">
          <thead>
            <tr>
              <th>{{ $t("common.company") }}</th>
              <th>{{ $t("cnmc.company_order") }}</th>
              <th>GOs</th>
              <th>{{ $t("cnmc.progress") }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="order in orders" :key="order.id">
              <td>{{ order.company_name }}</td>
              <td>#{{ order.id }}</td>
              <td>{{ order.expected.total_mwh }}</td>
              <td>
                {{ order.status }} ·
                {{
                  order.verified
                    ? $t("cnmc.export_verified")
                    : $t("cnmc.awaiting_verification")
                }}
              </td>
              <td>
                <button
                  class="btn btn-outline-primary btn-sm"
                  :disabled="busy"
                  @click="review(order)"
                >
                  {{ $t("cnmc.review") }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="selected" class="border-top pt-3">
        <h6>
          #{{ selected.id }} · {{ $t("cnmc.receiving_account") }} #{{
            selected.expected.receiving_connection_id
          }}
        </h6>
        <ul>
          <li
            v-for="plant in selected.expected.expected_plants"
            :key="plant.name"
          >
            {{ plant.name }} · {{ plant.go_count }} GOs
          </li>
        </ul>
        <div v-if="selected.accepted" class="alert alert-success">
          <p>
            {{ selected.accepted.accepted_at }} · {{ selected.accepted.reason }}
          </p>
          <ul class="mb-0">
            <li v-for="row in selected.accepted.certificates" :key="row.id">
              #{{ row.id }} · {{ row.count }} GOs ·
              {{ row.reviewed_device_name || row.device_name }} ·
              {{ formatDateTime(row.imported_at) }}
            </li>
          </ul>
        </div>
        <div v-else>
          <div class="table-responsive">
            <table class="table table-padded table-sm">
              <thead>
                <tr>
                  <th></th>
                  <th>{{ $t("cnmc.device") }}</th>
                  <th>GOs</th>
                  <th>{{ $t("cnmc.imported_at") }}</th>
                  <th>{{ $t("cnmc.production_months") }}</th>
                  <th>{{ $t("cnmc.reviewed_name") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in inventory" :key="row.id">
                  <td>
                    <input
                      v-model="selectedIds"
                      type="checkbox"
                      :value="row.id"
                      :aria-label="String(row.id)"
                    />
                  </td>
                  <td>#{{ row.id }} {{ row.name }}</td>
                  <td>{{ row.count }}</td>
                  <td>{{ formatDateTime(row.imported_at) }}</td>
                  <td>{{ row.start }} – {{ row.end }} · {{ row.origin }}</td>
                  <td>
                    <select
                      v-model="aliases[row.id]"
                      class="form-select form-select-sm"
                      :aria-label="$t('cnmc.reviewed_name')"
                    >
                      <option value="">
                        {{ $t("cnmc.use_registry_name") }}
                      </option>
                      <option
                        v-for="plant in selected.expected.expected_plants"
                        :key="plant.name"
                        :value="plant.name"
                      >
                        {{ plant.name }}
                      </option>
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>{{ selectedCount }} / {{ selected.expected.total_mwh }} GOs</p>
          <CnmcReasonForm
            :key="selected.id"
            :label="$t('cnmc.correction_reason')"
            :submit-label="$t('cnmc.accept_inventory')"
            :disabled="
              busy ||
              !selected.verified ||
              selectedCount !== selected.expected.total_mwh
            "
            button-class="btn-primary"
            @submit="accept"
          />
        </div>
      </div>
    </div>
  </section>
</template>
